/*
 * reviewDiff — Wort-Granularitaet-Diff fuer das Delta-Review.
 *
 * Erzeugt aus Original- und Proposed-Text einen "Composite-Text", in dem
 * unveraenderte, geloeschte und hinzugefuegte Spans sequentiell auftauchen.
 * Der Composite wird im EditorView durch denselben Word-Wrap geschickt wie
 * normaler Paragraph-Text — dadurch sind Original und Vorschlag stabil
 * vereint, der Toggle aendert nur Kontrast/Strikethrough, nicht Layout.
 *
 * Beispiel:
 *   computeReviewDiff('Es war ein kalter Morgen', 'Es war ein trüber Morgen.')
 *   → composite: 'Es war ein kalter trüber Morgen.'
 *     ranges: [
 *       { start: 0,  end: 11, kind: 'common' },     // 'Es war ein '
 *       { start: 11, end: 18, kind: 'remove' },     // 'kalter ' (mit Folge-Space)
 *       { start: 18, end: 25, kind: 'add'    },     // 'trüber '
 *       { start: 25, end: 31, kind: 'common' },     // 'Morgen'
 *       { start: 31, end: 32, kind: 'add'    }      // '.'
 *     ]
 *
 * Format-faehige API (opts):
 *   computeReviewDiff(orig, prop, { originalLinks, proposedLinks, separatorMap })
 *   loest [[@id]]-Marker auf beiden Seiten zu Klartext-Labels auf, bevor das
 *   Diff laeuft — Composite enthaelt dann lesbare Labels statt Hash-IDs.
 *
 *   Pro Composite-Char trackt die Funktion zwei Source-Offset-Maps:
 *     sourceOriginal[i]  — Display-Offset im displayOriginal oder -1
 *     sourceProposed[i]  — Display-Offset im displayProposed oder -1
 *   Renderer (buildCompositeFormatRuns) nutzt diese Maps + dataToDisplay-Maps,
 *   um pro Composite-Position das passende Inline-Format aus den FormatSpans
 *   beider Seiten zu lesen — je nach activeView.
 *
 * Algorithmus: Tokenize an Wortgrenzen (Whitespace als eigener Token-Typ),
 * LCS, dann Walk der zwei Token-Listen mit Range-Akkumulation.
 *
 * Granularitaet: Wort. Bei sehr kurzen Aenderungen (Tippfehler) ist Wort-
 * Granularitaet etwas grob — fuer v1 reicht das, Zeichen-Diff koennen wir
 * spaeter ergaenzen.
 */

import { buildRichTextMapping } from '../RichTextOps.js'

/**
 * Range im Composite-Text.
 * @typedef {{start: number, end: number, kind: 'common'|'remove'|'add'}} ReviewRange
 */

/**
 * Splittet einen Text in Tokens (Wort-/Whitespace-/Punctuation-Sequenzen).
 * Beispiel: "Hello, world!" → ['Hello', ',', ' ', 'world', '!']
 * Whitespace wird als eigener Token gehalten, damit der Composite-Spacing
 * nicht durch den Diff-Walk verzerrt wird.
 * @param {string} text
 * @returns {string[]}
 */
export function tokenize(text) {
    if (!text) return []
    const tokens = []
    let buffer = ''
    let bufferKind = null   // 'word' | 'space' | 'punct'

    const flush = () => {
        if (buffer.length > 0) {
            tokens.push(buffer)
            buffer = ''
        }
    }

    for (const ch of text) {
        const kind = /\s/.test(ch) ? 'space' : (/[\p{L}\p{N}_]/u.test(ch) ? 'word' : 'punct')
        if (kind !== bufferKind) {
            flush()
            bufferKind = kind
        }
        // Punctuation: jeder Punctuation-Char ist sein eigener Token, damit ein
        // Komma im Original und ein Punkt im Vorschlag sauber als separate
        // Tokens gediffed werden ("end," vs. "end." → zwei Diff-Tokens).
        if (kind === 'punct') {
            flush()
            tokens.push(ch)
            bufferKind = null
            continue
        }
        buffer += ch
    }
    flush()
    return tokens
}

/**
 * Klassischer LCS (Longest Common Subsequence) ueber Token-Arrays.
 * Liefert die LCS-Token-Reihenfolge zurueck — wir reklassifizieren beim
 * Walk dann gegen die Originale.
 * @param {string[]} a
 * @param {string[]} b
 * @returns {string[]}
 */
export function lcs(a, b) {
    const m = a.length, n = b.length
    if (m === 0 || n === 0) return []
    // Standard-DP-Tabelle (m+1) × (n+1). Bei sehr langen Texten waere Hirschberg
    // sparsamer — fuer Paragraph-Laengen (~500 Wörter) reicht O(mn).
    const dp = Array.from({ length: m + 1 }, () => new Int32Array(n + 1))
    for (let i = 0; i < m; i++) {
        for (let j = 0; j < n; j++) {
            if (a[i] === b[j]) dp[i + 1][j + 1] = dp[i][j] + 1
            else dp[i + 1][j + 1] = Math.max(dp[i + 1][j], dp[i][j + 1])
        }
    }
    // Backtrack
    const out = []
    let i = m, j = n
    while (i > 0 && j > 0) {
        if (a[i - 1] === b[j - 1]) { out.push(a[i - 1]); i--; j-- }
        else if (dp[i - 1][j] >= dp[i][j - 1]) i--
        else j--
    }
    return out.reverse()
}

/**
 * Berechnet den Diff zwischen Original und Proposed und liefert einen
 * Composite-Text plus Range-Tags. Aufeinanderfolgende Ranges desselben
 * Kinds werden zusammengefasst.
 *
 * Block-Reihenfolge: Removes erscheinen *vor* den Adds eines Bursts —
 *   "the [cold dark][icy gloomy] night"  statt  "the [cold][icy] [dark][gloomy] night"
 * Whitespace-only common-Tokens, die zwischen Diff-Tokens stehen, werden
 * an die pendings angehaengt (nicht als common geflusht), damit eine
 * Folge wechselnder remove/add-Wörter zu einem zusammenhaengenden Burst
 * verschmilzt. Erst der naechste *Wort*-common flusht den Burst.
 *
 * @param {string} original
 * @param {string} proposed
 * @param {object} [opts]
 * @param {Array<{offset:number,targetId:*}>} [opts.originalLinks]
 * @param {Array<{offset:number,targetId:*}>} [opts.proposedLinks]
 * @param {Map<*,{label:string,isBroken?:boolean}>} [opts.separatorMap]
 * @returns {{
 *   composite: string,
 *   ranges: ReviewRange[],
 *   original: string,
 *   proposed: string,
 *   displayOriginal: string,
 *   displayProposed: string,
 *   sourceOriginal: Int32Array,
 *   sourceProposed: Int32Array,
 *   origDataToDisplay: Int32Array|null,
 *   propDataToDisplay: Int32Array|null,
 *   origDisplayToData: Int32Array|null,
 *   propDisplayToData: Int32Array|null
 * }}
 */
export function computeReviewDiff(original, proposed, opts = {}) {
    const { originalLinks = null, proposedLinks = null, separatorMap = null } = opts

    // Phase A: Display-Texte mit aufgeloesten Section-Link-Labels. Wenn keine
    // Links/separatorMap, liefert buildRichTextMapping null — wir fallen zurueck
    // auf den Roh-Text (= heutiges Verhalten).
    const origMap = buildRichTextMapping(original, originalLinks, separatorMap)
    const propMap = buildRichTextMapping(proposed, proposedLinks, separatorMap)
    const displayOriginal = origMap ? origMap.displayText : original
    const displayProposed = propMap ? propMap.displayText : proposed

    // Phase B: Token-Diff auf den Display-Texten.
    const a = tokenize(displayOriginal)
    const b = tokenize(displayProposed)
    const common = lcs(a, b)

    // Token-Boundaries (Display-Offsets) vorrechnen, damit wir pro Token sein
    // Source-Offset im Display-Text kennen. Damit kann der Renderer spaeter
    // fuer eine Composite-Position die zugehoerigen FormatSpans der jeweiligen
    // Seite nachschlagen.
    const aOff = new Int32Array(a.length + 1)
    for (let i = 0; i < a.length; i++) aOff[i + 1] = aOff[i] + a[i].length
    const bOff = new Int32Array(b.length + 1)
    for (let i = 0; i < b.length; i++) bOff[i + 1] = bOff[i] + b[i].length

    let composite = ''
    /** @type {ReviewRange[]} */
    const ranges = []
    const srcO = []   // pro Composite-Char: Display-Offset im displayOriginal oder -1
    const srcP = []   // pro Composite-Char: Display-Offset im displayProposed oder -1
    /** Pending-Listen: {text, sourceOff} pro Token */
    let pendingRemove = []
    let pendingAdd = []

    const isWhitespaceOnly = (s) => s.length > 0 && /^\s+$/.test(s)

    const writeChar = (ch, oOff, pOff) => {
        composite += ch
        srcO.push(oOff)
        srcP.push(pOff)
    }

    /**
     * Schreibt pendingRemove und pendingAdd in den Composite (Removes zuerst).
     * An der Naht zwischen Remove- und Add-Block sowie zwischen Add-Block und
     * nachfolgendem Composite-Inhalt wird ein Trenner-Space als 'common' Range
     * eingefuegt, falls keiner der angrenzenden Texte bereits ein Whitespace
     * mitbringt — sonst kleben Original-Wort und Vorschlags-Wort aneinander
     * ("coldicy" statt "cold icy").
     */
    const flushPending = () => {
        if (pendingRemove.length > 0) {
            const start = composite.length
            for (const seg of pendingRemove) {
                for (let k = 0; k < seg.text.length; k++) {
                    writeChar(seg.text[k], seg.sourceOff + k, -1)
                }
            }
            ranges.push({ start, end: composite.length, kind: 'remove' })
            pendingRemove = []
        }
        if (pendingAdd.length > 0) {
            const lastRange = ranges[ranges.length - 1]
            const compositeEndsWithSpace = composite.length > 0 && /\s$/.test(composite)
            const firstAddText = pendingAdd[0].text
            const addStartsWithSpace = /^\s/.test(firstAddText)
            if (lastRange?.kind === 'remove' && !compositeEndsWithSpace && !addStartsWithSpace) {
                const sepStart = composite.length
                writeChar(' ', -1, -1)
                ranges.push({ start: sepStart, end: composite.length, kind: 'common' })
            }
            const start = composite.length
            for (const seg of pendingAdd) {
                for (let k = 0; k < seg.text.length; k++) {
                    writeChar(seg.text[k], -1, seg.sourceOff + k)
                }
            }
            ranges.push({ start, end: composite.length, kind: 'add' })
            pendingAdd = []
        }
    }

    /**
     * Common-Token emittieren. WS-only common, der mitten im Burst auftaucht,
     * wird ans pending gehaengt — sonst zerschnitte er den Burst.
     */
    const emitCommon = (token, aIdx, bIdx) => {
        const inBurst = pendingRemove.length > 0 || pendingAdd.length > 0
        if (inBurst && isWhitespaceOnly(token)) {
            // WS gehoert zu beiden Seiten (a und b haben das Whitespace-Token gleich).
            // Aber nur an die Seiten anhaengen, die schon nicht-leer sind, damit
            // aus einer reinen Loeschung nicht plötzlich ein "add" entsteht.
            if (pendingRemove.length > 0) pendingRemove.push({ text: token, sourceOff: aOff[aIdx] })
            if (pendingAdd.length > 0) pendingAdd.push({ text: token, sourceOff: bOff[bIdx] })
            return
        }
        flushPending()
        const start = composite.length
        const oStart = aOff[aIdx]
        const pStart = bOff[bIdx]
        for (let k = 0; k < token.length; k++) {
            writeChar(token[k], oStart + k, pStart + k)
        }
        const last = ranges[ranges.length - 1]
        if (last && last.kind === 'common' && last.end === start) {
            last.end = composite.length
        } else {
            ranges.push({ start, end: composite.length, kind: 'common' })
        }
    }

    let ai = 0, bi = 0
    for (const c of common) {
        while (ai < a.length && a[ai] !== c) {
            pendingRemove.push({ text: a[ai], sourceOff: aOff[ai] })
            ai++
        }
        while (bi < b.length && b[bi] !== c) {
            pendingAdd.push({ text: b[bi], sourceOff: bOff[bi] })
            bi++
        }
        if (ai < a.length && bi < b.length && a[ai] === c && b[bi] === c) {
            emitCommon(c, ai, bi)
            ai++
            bi++
        }
    }
    // Reste nach LCS
    while (ai < a.length) {
        pendingRemove.push({ text: a[ai], sourceOff: aOff[ai] })
        ai++
    }
    while (bi < b.length) {
        pendingAdd.push({ text: b[bi], sourceOff: bOff[bi] })
        bi++
    }
    flushPending()

    return {
        composite,
        ranges,
        original,
        proposed,
        displayOriginal,
        displayProposed,
        sourceOriginal: Int32Array.from(srcO),
        sourceProposed: Int32Array.from(srcP),
        origDataToDisplay: origMap ? origMap.dataToDisplay : null,
        propDataToDisplay: propMap ? propMap.dataToDisplay : null,
        origDisplayToData: origMap ? origMap.displayToData : null,
        propDisplayToData: propMap ? propMap.displayToData : null
    }
}

/**
 * Baut FormatRuns ueber die volle Composite-Laenge fuer einen gegebenen
 * activeView. Pro Composite-Position ermittelt der Helper das aktive
 * Inline-Format aus den FormatSpans der passenden Seite (sourceProposed bzw.
 * sourceOriginal aus dem Diff-Output) und kombiniert es mit dem Diff-Akzent-
 * Style (KEEP/SHOWN/STRIKE).
 *
 * Common-Bereiche folgen activeView: bei 'proposed' gewinnen die proposed-
 * Spans, bei 'original' die original-Spans — Toggle wechselt also konsistent
 * Inhalt UND Inline-Format (Boris-Entscheidung 2026-05-02).
 *
 * Output-Shape ist kompatibel zu renderProportionalLine (start/end im
 * Composite-Raum, bold/italic/underline/strikethrough/mono-Flags + style-String).
 *
 * @param {object} diff           Output von computeReviewDiff
 * @param {Array<{start:number,end:number,type:string}>} originalFormats  in Daten-Raum (orig)
 * @param {Array<{start:number,end:number,type:string}>} proposedFormats  in Daten-Raum (prop)
 * @param {'proposed'|'original'} activeView
 * @param {(kind:'common'|'add'|'remove', activeView:string) => string} styleForKind
 *        Liefert die Diff-Style-ID je Range-Kind + activeView (gepusht von
 *        EditorView, damit dieser Helper Renderer-agnostisch bleibt).
 * @param {object} [styleDefs]    Optional: Style → {isBold,isItalic,...} Tabelle,
 *                                damit Diff-Style auch Strike-Flag setzen kann
 *                                (REVIEW_STRIKE traegt isStrikethrough=true).
 * @returns {Array<{start:number,end:number,bold:boolean,italic:boolean,underline:boolean,strikethrough:boolean,mono:boolean,style:string}>}
 */
export function buildCompositeFormatRuns(diff, originalFormats, proposedFormats, activeView, styleForKind, styleDefs = null) {
    const { composite, ranges, sourceOriginal, sourceProposed,
            origDataToDisplay, propDataToDisplay,
            origDisplayToData, propDisplayToData,
            displayOriginal, displayProposed } = diff
    if (!composite || composite.length === 0) return []

    // FormatSpans in Display-Koordinaten projizieren (falls Links die Indizes
    // verschoben haben). Ohne dataToDisplay sind Daten- und Display-Raum
    // identisch — direkter Span-Use.
    const origSpansDisplay = projectSpansToDisplay(originalFormats, origDataToDisplay, displayOriginal.length)
    const propSpansDisplay = projectSpansToDisplay(proposedFormats, propDataToDisplay, displayProposed.length)

    // Pro Composite-Char das Inline-Format der "richtigen" Seite ermitteln.
    // Welche Seite ist richtig?
    //   Range-Kind 'remove' → Original-Spans an sourceOriginal[i]
    //   Range-Kind 'add'    → Proposed-Spans an sourceProposed[i]
    //   Range-Kind 'common' → activeView entscheidet:
    //                          'original' → original-Spans
    //                          'proposed' → proposed-Spans
    //   Synthetischer Naht-Space (sourceO=sourceP=-1) → kein Inline-Format
    // Slot-Layout: [bold, italic, underline, strike, mono] pro Char (5 Flags)
    const flags = new Uint8Array(composite.length * 5)
    let rangeIdx = 0
    for (let i = 0; i < composite.length; i++) {
        while (rangeIdx < ranges.length && ranges[rangeIdx].end <= i) rangeIdx++
        const kind = ranges[rangeIdx]?.kind || 'common'
        let useOriginal
        if (kind === 'remove') useOriginal = true
        else if (kind === 'add') useOriginal = false
        else useOriginal = activeView === 'original'

        const spans = useOriginal ? origSpansDisplay : propSpansDisplay
        const off = useOriginal ? sourceOriginal[i] : sourceProposed[i]
        if (off < 0) continue   // synthetischer Char ohne Source

        const f = formatFlagsAt(spans, off)
        flags[i * 5]     = f.bold     ? 1 : 0
        flags[i * 5 + 1] = f.italic   ? 1 : 0
        flags[i * 5 + 2] = f.underline ? 1 : 0
        flags[i * 5 + 3] = f.strike   ? 1 : 0
        flags[i * 5 + 4] = f.mono     ? 1 : 0
    }

    // Pro Range: Diff-Style nachschlagen, dann Runs aufbauen, indem wir
    // innerhalb der Range bei jedem Flag-Wechsel einen neuen Run beginnen.
    const runs = []
    for (const r of ranges) {
        const style = styleForKind(r.kind, activeView)
        const styleDef = styleDefs?.[style] || {}
        // Diff-Style traegt selbst Strikethrough (REVIEW_STRIKE) — als Floor
        // ueber die Range. Inline-Strikethrough kann es zusaetzlich noch geben.
        const styleStrike = !!styleDef.isStrikethrough
        const styleBold = !!styleDef.isBold
        const styleItalic = !!styleDef.isItalic
        const styleUnderline = !!styleDef.isUnderline

        let runStart = r.start
        let runBold, runItalic, runUnderline, runStrike, runMono

        for (let i = r.start; i < r.end; i++) {
            const b = (flags[i * 5]     === 1) || styleBold
            const it = (flags[i * 5 + 1] === 1) || styleItalic
            const u = (flags[i * 5 + 2] === 1) || styleUnderline
            const s = (flags[i * 5 + 3] === 1) || styleStrike
            const m = (flags[i * 5 + 4] === 1)   // Diff-Color dominiert: kein styleMono-Floor

            if (i === r.start) {
                runBold = b; runItalic = it; runUnderline = u; runStrike = s; runMono = m
                continue
            }
            if (b !== runBold || it !== runItalic || u !== runUnderline || s !== runStrike || m !== runMono) {
                runs.push({ start: runStart, end: i,
                            bold: runBold, italic: runItalic,
                            underline: runUnderline, strikethrough: runStrike,
                            mono: runMono, style })
                runStart = i
                runBold = b; runItalic = it; runUnderline = u; runStrike = s; runMono = m
            }
        }
        if (r.end > r.start) {
            runs.push({ start: runStart, end: r.end,
                        bold: runBold, italic: runItalic,
                        underline: runUnderline, strikethrough: runStrike,
                        mono: runMono, style })
        }
    }
    return runs
}

/**
 * Projiziert FormatSpans aus dem Daten-Raum (mit ￼-Linkmarkern) in den
 * Display-Raum (mit aufgeloesten Labels). Wenn dataToDisplay null ist (keine
 * Links), liefert die Funktion die Spans unveraendert.
 */
function projectSpansToDisplay(spans, dataToDisplay, displayLength) {
    if (!spans || spans.length === 0) return []
    if (!dataToDisplay) return spans
    const out = []
    for (const span of spans) {
        const ds = span.start < dataToDisplay.length ? dataToDisplay[span.start] : displayLength
        const de = span.end <= dataToDisplay.length ? dataToDisplay[span.end] : displayLength
        if (de > ds) out.push({ start: ds, end: de, type: span.type })
    }
    return out
}

/**
 * Ermittelt aktive Format-Flags an einer Display-Offset-Position. O(n) ueber
 * die Spans — fuer typische Paragraph-Span-Counts (<10) deutlich guenstiger
 * als ein Sweep-Line-Index aufzubauen.
 */
function formatFlagsAt(spans, offset) {
    let bold = false, italic = false, underline = false, strike = false, mono = false
    for (const s of spans) {
        if (offset < s.start || offset >= s.end) continue
        if (s.type === 'bold') bold = true
        else if (s.type === 'italic') italic = true
        else if (s.type === 'underline') underline = true
        else if (s.type === 'strikethrough') strike = true
        else if (s.type === 'code') mono = true
    }
    return { bold, italic, underline, strike, mono }
}

/**
 * Injiziert virtuelle Bold-/Italic-Floor-Spans fuer Heading- bzw. Blockquote-
 * Bloecke. Der Composite-Renderer nutzt Diff-Akzent-Styles (EDITOR_REVIEW_*),
 * die selbst weder bold noch italic sind — ohne diesen Floor erbt ein Heading
 * im Review nur die Schriftgroesse, ein Blockquote nur den Zeilenabstand.
 *
 * Der gleiche Floor wird im Rewrite-, Delete- und Insert-Pfad gebraucht; vor
 * der Extraktion lebte die Logik dreifach inline. Helper laesst die originalen
 * `formats` unveraendert (kopiert vor dem Push).
 *
 * @param {Array<{start:number,end:number,type:string}>|null} formats
 * @param {number} textLen
 * @param {number} headingLevel  — 0 = kein Heading
 * @param {boolean} blockquote
 * @returns {Array<{start:number,end:number,type:string}>}
 */
export function injectBlockFloor(formats, textLen, headingLevel, blockquote) {
    const out = formats ? [...formats] : []
    if ((headingLevel || 0) > 0) out.push({ start: 0, end: textLen, type: 'bold' })
    if (blockquote) out.push({ start: 0, end: textLen, type: 'italic' })
    return out
}

/**
 * Slice von vorberechneten FormatRuns auf einen Zeilen-Bereich [lineStart..lineEnd).
 * Lokale Koordinaten (run.start - lineStart). Trivial, aber haeufig genutzt im
 * Composite-Renderer fuer pro-Zeile-Slicing.
 */
export function sliceFormatRunsForLine(fullRuns, lineStart, lineEnd) {
    const out = []
    for (const r of fullRuns) {
        if (r.end <= lineStart || r.start >= lineEnd) continue
        const ls = Math.max(r.start, lineStart) - lineStart
        const le = Math.min(r.end, lineEnd) - lineStart
        out.push({
            start: ls, end: le,
            bold: r.bold, italic: r.italic,
            underline: r.underline, strikethrough: r.strikethrough,
            mono: r.mono || false,
            style: r.style
        })
    }
    return out
}
